/**
 * Canonical theme-id registry — the single source of truth the SERVER trusts
 * when validating a persisted theme (PATCH /users/me, batch 3 of the July 2026
 * design handoff).
 *
 * The rich registry (labels, emotions, chrome hexes, apply logic) lives in the
 * web app at apps/web/src/lib/theme.ts. The API cannot import from apps/web, so
 * the bare id list is duplicated here. A unit test in the web app
 * (apps/web/src/lib/theme.shared.test.ts) asserts the web THEMES registry ids
 * exactly match this array, so the two can never silently drift.
 *
 * `light` (Daylight) is the app default at the launch flip; the DB column is
 * nullable and the app treats a null theme as 'light'.
 */
// Order matches the design handoff's CF_THEMES display order (Daylight leads,
// Midnight last, Golden hour before Sage) — the web THEMES registry mirrors it
// and the appearance picker renders the registry directly. A unit test
// (apps/web/src/lib/theme.shared.test.ts) pins the two lists order-exact.
export const THEME_IDS = [
  "light",
  "dawn",
  "rose",
  "golden",
  "sage",
  "moonstone",
  "midnight",
] as const;

export type ThemeId = (typeof THEME_IDS)[number];

const THEME_ID_SET = new Set<string>(THEME_IDS);

/** Type guard: is `value` one of the registered theme ids? */
export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === "string" && THEME_ID_SET.has(value);
}

/**
 * The four mini-preview swatch colors per theme — page background, lifted card
 * surface, default accent, and text bar. These are the SAME literals the web
 * appearance picker renders (apps/web/src/lib/theme.ts `THEMES[].swatch`), lifted
 * here so the API — which cannot import from apps/web — can resolve a sender's
 * theme to real hexes when rendering a shared reading card (batch 6). A web unit
 * test (apps/web/src/lib/theme.shared.test.ts) asserts the web registry's swatch
 * fields deep-equal this map, so the two can never silently drift.
 *
 * Values copied VERBATIM from the design handoff's CF_THEMES[].sw — do NOT
 * re-theme or paraphrase (a Rose swatch must always look like Rose).
 */
export interface ThemeSwatch {
  /** Page background. */
  page: string;
  /** Lifted card surface. */
  card: string;
  /** Default accent color. */
  accent: string;
  /** Primary text color. */
  text: string;
}

export const THEME_SWATCHES: Record<ThemeId, ThemeSwatch> = {
  light: { page: "#f3ead4", card: "#fff8e4", accent: "#4a6b88", text: "#2a2417" },
  dawn: { page: "#efeaf3", card: "#fbf8fd", accent: "#6557d1", text: "#221d33" },
  rose: { page: "#f6ebe8", card: "#fff7f4", accent: "#b13d55", text: "#33201d" },
  golden: { page: "#f7ecd9", card: "#fff8e8", accent: "#96600e", text: "#2e2413" },
  sage: { page: "#eef0e6", card: "#fbfcf4", accent: "#3f6b4f", text: "#232a1f" },
  moonstone: { page: "#e9edf2", card: "#fafcff", accent: "#3f648a", text: "#1f2733" },
  midnight: { page: "#0d0d12", card: "#1a1a26", accent: "#7a9ab5", text: "#e8e2d9" },
};
